import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scene } from '../services/api.ts';
import { useStory } from '../contexts/StoryContext.tsx';
import { useSceneImagesQuery } from '../hooks/useSceneImagesQuery.ts';

// Extended interface for dual status tracking
interface SceneWithStatus extends Scene {
  description_status: 'generating' | 'completed';
  showDescription?: boolean;
  end_frame_url?: string | null;
  start_frame_prompt?: string;
  end_frame_prompt?: string;
}

type FrameType = 'start' | 'end';

interface FrameCardModel {
  shotNumber: number;
  type: FrameType;
  imageUrl: string | null;
  prompt: string;
  status: 'generating' | 'completed';
}

// Helper (simplified): statusCount > have > 1 placeholder on "scenes"
const computeSlotCount = (
  statusCount?: number,
  have = 0,
  currentStep?: string
) => {
  if (typeof statusCount === 'number' && statusCount > 0) return statusCount;
  if (have > 0) return have;
  return currentStep === 'scenes' ? 1 : 0;
};

const SceneBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { selectedStoryId, storyStatus } = useStory();

  const [selectedScene, setSelectedScene] = useState<SceneWithStatus | null>(null);
  const [selectedFrame, setSelectedFrame] = useState<FrameCardModel | null>(null);

  const {
    data: rawScenes = [],
    isLoading: imgsLoading,
    isFetching: imgsFetching,
    error: imgsError,
  } = useSceneImagesQuery(selectedStoryId, storyStatus);

  // Build display scenes (prefer description, then image_prompt)
  const scenes: SceneWithStatus[] = useMemo(() => {
    return (rawScenes || [])
      .map((img, index) => {
        const desc = img.description ?? img.image_prompt ?? '';
        return {
          id: img.id ?? img.scene_number ?? index + 1,
          scene_number: img.scene_number ?? index + 1,
          description: desc,
          image_prompt: img.image_prompt ?? '',
          image_url: img.starting_frame_url ?? null,
          end_frame_url: img.end_frame_url ?? null,
          start_frame_prompt: img.start_frame_prompt ?? '',
          end_frame_prompt: img.end_frame_prompt ?? '',
          status: img.status ?? 'generating',
          description_status: desc?.trim() ? 'completed' : 'generating',
        } as SceneWithStatus;
      })
      .sort((a, b) => a.scene_number - b.scene_number);
  }, [rawScenes]);

  // Build separate frame cards per shot (start/end)
  const frameCards: FrameCardModel[] = useMemo(() => {
    const out: FrameCardModel[] = [];
    (scenes || []).forEach(s => {
      // Start frame card
      out.push({
        shotNumber: s.scene_number,
        type: 'start',
        imageUrl: s.image_url ?? null,
        prompt: s.start_frame_prompt ?? '',
        status: s.image_url && s.status === 'completed' ? 'completed' : (!s.image_url ? 'generating' : s.status),
      });
      // End frame card
      out.push({
        shotNumber: s.scene_number,
        type: 'end',
        imageUrl: s.end_frame_url ?? null,
        prompt: s.end_frame_prompt ?? '',
        status: s.end_frame_url ? 'completed' : 'generating',
      });
    });
    // Sort by shot, then start before end
    return out.sort((a, b) => a.shotNumber === b.shotNumber ? (a.type === 'start' ? -1 : 1) : a.shotNumber - b.shotNumber);
  }, [scenes]);

  // Scroll to top on story change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedStoryId]);

  // Early exit: no story selected
  if (!selectedStoryId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Story Selected</h2>
          <p className="text-gray-600 mb-4">Please select a story to view its scenes.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Stories
          </button>
        </div>
      </div>
    );
  }

  // Initial loading (page-level)
  if (imgsLoading && scenes.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Scenes</h2>
          <p className="text-gray-600">Preparing your story&apos;s scenes...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (imgsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Scenes</h2>
          <p className="text-gray-600 mb-4">{(imgsError as Error).message || 'Failed to load scenes'}</p>
        </div>
      </div>
    );
  }

  const totalSlots = (computeSlotCount(
    storyStatus?.shots_count,
    scenes.length,
    storyStatus?.current_step
  ) * 2) || frameCards.length;

  // Frame card (separate card per start/end)
  const FrameCard: React.FC<{ frame: FrameCardModel }> = ({ frame }) => {
    const [imgError, setImgError] = useState(false);
    const isGenerating = frame.status === 'generating' || !frame.imageUrl;
    const label = frame.type === 'start' ? 'Start' : 'End';

    return (
      <div
        className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 border border-gray-200 cursor-pointer"
        onClick={() => setSelectedFrame(frame)}
      >
        <div className="mb-0 rounded-t-xl overflow-hidden bg-gray-100">
          <div className="aspect-video flex items-center justify-center relative">
            {frame.imageUrl && !imgError ? (
              <img
                src={frame.imageUrl}
                alt={`Shot ${frame.shotNumber} - ${label} Frame`}
                className="w-full h-full object-contain"
                loading="lazy"
                decoding="async"
                onError={() => setImgError(true)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-400">
                {isGenerating ? (
                  <div role="status" aria-live="polite">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mb-1"></div>
                    <div className="text-xs">{label}...</div>
                  </div>
                ) : (
                  <div className="text-2xl">🖼️</div>
                )}
              </div>
            )}
            <div className={`absolute top-1 ${label === 'Start' ? 'left-1' : 'right-1'} bg-black bg-opacity-50 text-white text-xs px-1 rounded`}>
              {label}
            </div>
          </div>
        </div>

        <div className="p-3 border-t">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-800">Shot {frame.shotNumber} — {label}</h3>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isGenerating ? 'bg-yellow-100 text-yellow-800 animate-pulse' : 'bg-green-100 text-green-800'
              }`}
            >
              {isGenerating ? `${label}` : `${label} ✓`}
            </span>
          </div>
          {frame.prompt ? (
            <div className="bg-gray-50 rounded p-2 text-xs text-gray-700 whitespace-pre-wrap">
              {frame.prompt}
            </div>
          ) : (
            <div className="text-xs text-gray-400">No prompt available</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
      </div>

      {/* Frames Grid (separate card per start/end) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {frameCards.length > 0
            ? frameCards.map((f, idx) => (
                <FrameCard key={`${f.shotNumber}-${f.type}-${idx}`} frame={f} />
              ))
            : Array.from({ length: totalSlots }).map((_, i) => (
                <div
                  key={`placeholder-${i}`}
                  className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  <div className="aspect-video bg-gray-100 flex items-center justify-center">
                    <div className="text-center" role="status" aria-live="polite">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <div className="text-sm text-gray-500">Generating Frame...</div>
                      <span className="sr-only">Generating frame image, please wait...</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-800">Frame {i + 1}</h3>
                      <div className="w-4 h-4 bg-yellow-400 rounded-full animate-pulse"></div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div className="w-full animate-pulse bg-gradient-to-r from-purple-400 to-blue-400 h-2"></div>
                    </div>
                  </div>
                </div>
              ))}
        </div>
      </div>

      {/* Scene Image Modal (legacy) */}
      {selectedScene && !selectedScene.showDescription && (
        <div
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300"
          onClick={() => setSelectedScene(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Scene {selectedScene.scene_number}</h2>
              <button
                onClick={() => setSelectedScene(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4">
                {selectedScene.image_url ? (
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <img
                      src={selectedScene.image_url}
                      alt={`Scene ${selectedScene.scene_number}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-gray-200 rounded-lg flex items-center justify-center">
                    <div className="text-center" role="status" aria-live="polite">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                      <p className="text-gray-600 font-medium">Generating Scene Image...</p>
                      <p className="text-sm text-gray-500 mt-2">This scene is being processed</p>
                      <span className="sr-only">Generating scene image, please wait...</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Frame Modal */}
      {selectedFrame && (
        <div
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300"
          onClick={() => setSelectedFrame(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Shot {selectedFrame.shotNumber} — {selectedFrame.type === 'start' ? 'Start' : 'End'} Frame</h2>
              <div className="flex items-center gap-2">
                {selectedFrame.imageUrl && (
                  <a
                    href={selectedFrame.imageUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Open Full Size
                  </a>
                )}
                <button
                  onClick={() => setSelectedFrame(null)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-4 grid grid-cols-1 gap-4">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                {selectedFrame.imageUrl ? (
                  <img
                    src={selectedFrame.imageUrl}
                    alt={`Shot ${selectedFrame.shotNumber} ${selectedFrame.type}`}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <div className="text-center" role="status" aria-live="polite">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                      <p>Generating Frame...</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="bg-gray-50 rounded p-3">
                <h3 className="font-semibold text-gray-800 mb-2">Prompt</h3>
                <pre className="whitespace-pre-wrap text-xs text-gray-700">{selectedFrame.prompt || 'No prompt available'}</pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {selectedScene && selectedScene.showDescription && (
        <div
          className="fixed inset-0 lg:left-64 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 transition-opacity duration-300"
          onClick={() => setSelectedScene(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Scene {selectedScene.scene_number} - Description
              </h2>
              <button
                onClick={() => setSelectedScene(null)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              <div className="prose max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  {selectedScene.description || 'No description available for this scene.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SceneBuilder;
